import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./DoctorDirectory.css";
import drIsInLogo from "../../../assets/dr-is-in.png";

const API_BASE = (process.env.REACT_APP_API_BASE_URL || "https://doctorisin-genesis-1-0.onrender.com").replace(/\/+$/, "");

function adminToken() {
  return (
    localStorage.getItem("admin_token") ||
    localStorage.getItem("admin_access_token") ||
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    ""
  )
    .replace(/^Bearer\s+/i, "")
    .trim();
}

type DoctorRow = {
  id: string | number;
  name?: string | null;
  email?: string | null;
  specialties?: string | null;
  medical_field?: string | null;
  languages?: string | string[] | null;
  country?: string | null;
  state?: string | null;
  location?: string | null;
  website?: string | null;
  status?: string | null;
  photo_url?: string | null;
  bio?: string | null;
  medical_license_number?: string | null;
  retired?: boolean | null;
  is_online?: boolean | number | string | null;
  is_available?: boolean | number | string | null;
  emergency_consult_available?: boolean | number | string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type AvailabilityState = "available" | "busy" | "offline";

function cleanValue(v?: string | null) {
  const s = (v || "").trim();
  if (!s || s.toLowerCase() === "string") return "";
  return s;
}

function boolFromAny(value: unknown, fallback = false) {
  if (value === undefined || value === null || value === "") return fallback;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  const text = String(value).trim().toLowerCase();
  if (["1", "true", "yes", "y", "on", "online", "available", "on call", "on_call"].includes(text)) return true;
  if (["0", "false", "no", "n", "off", "offline", "unavailable", "retired"].includes(text)) return false;
  return fallback;
}

function normalizeWebsite(url?: string | null) {
  const raw = cleanValue(url);
  if (!raw) return "";
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
  return `https://${raw}`;
}

function absoluteUrl(url?: string | null) {
  const raw = cleanValue(url);
  if (!raw) return "";
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
  if (raw.startsWith("/")) return `${API_BASE}${raw}`;
  return raw;
}

function getAvailability(doctor: DoctorRow): AvailabilityState {
  const status = cleanValue(doctor.status).toLowerCase();
  if (doctor.retired || status === "retired" || status === "offline") return "offline";

  const online = boolFromAny(doctor.is_online, status === "on call" || status === "online" || status === "available");
  if (!online) return "offline";

  const available = boolFromAny(doctor.is_available, status !== "busy" && status !== "in session");
  return available ? "available" : "busy";
}

function availabilityLabel(state: AvailabilityState) {
  if (state === "available") return "AVAILABLE";
  if (state === "busy") return "BUSY";
  return "OFFLINE";
}

function formatLanguages(value: DoctorRow["languages"]) {
  if (Array.isArray(value)) return value.filter(Boolean).join(", ");
  return cleanValue(value || "") || "—";
}

export default function DoctorDirectory() {
  const navigate = useNavigate();
  const role = (localStorage.getItem("role") || "").toLowerCase();
  const isAdmin = role === "admin";

  const [docs, setDocs] = useState<DoctorRow[]>([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [profileDoctor, setProfileDoctor] = useState<DoctorRow | null>(null);
  const [connectDoctor, setConnectDoctor] = useState<DoctorRow | null>(null);
  const [consultMessage, setConsultMessage] = useState("");
  const [consultNotice, setConsultNotice] = useState("");

  const year = new Date().getFullYear();

  const logout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/login");
  };

  const handleBack = () => {
    if (isAdmin) navigate("/dashboard");
    else navigate(-1);
  };

  const handleProfile = (doctor: DoctorRow) => {
    if (isAdmin) {
      navigate(`/admin/doctors/${doctor.id}`, { state: { doctor } });
      return;
    }
    setProfileDoctor(doctor);
  };

  const handleConnect = (doctor: DoctorRow) => {
    setConsultMessage("");
    setConsultNotice("");
    setConnectDoctor(doctor);
  };

  const handleEdit = (doctor: DoctorRow) => {
    navigate(`/edit-doctor/${doctor.id}`, {
      state: { doctor, backTo: "/admin/doctors" },
    });
  };

  const sendConsultationRequest = () => {
    if (!connectDoctor) return;
    const message = consultMessage.trim();
    if (!message) {
      setConsultNotice("Enter a short consultation request before sending.");
      return;
    }

    // Version 1.1 foundation only. Realtime advisor-to-advisor routing will be wired next.
    setConsultNotice("Consultation interface is ready. Realtime advisor-to-advisor delivery will be connected in the next wiring step.");
  };

  async function loadDoctors() {
    setLoading(true);
    setErr("");

    try {
      const endpoint = isAdmin ? "/admin/doctors/roster" : "/public/doctors";
      const token = adminToken();
      const response = await fetch(`${API_BASE}${endpoint}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(isAdmin && token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      let payload: any = null;
      try {
        payload = await response.json();
      } catch {
        payload = null;
      }

      if (!response.ok) {
        throw new Error(payload?.detail || payload?.message || `Unable to load advisors. Status ${response.status}`);
      }

      const rows = Array.isArray(payload?.doctors)
        ? payload.doctors
        : Array.isArray(payload?.items)
          ? payload.items
          : Array.isArray(payload)
            ? payload
            : [];

      setDocs(rows as DoctorRow[]);
    } catch (e: any) {
      setErr(e?.message || "Unable to load advisors.");
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadDoctors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return docs;

    return docs.filter((d) => {
      const hay = [
        d.name,
        d.email,
        d.specialties,
        d.medical_field,
        Array.isArray(d.languages) ? d.languages.join(" ") : d.languages,
        d.country,
        d.state,
        d.location,
        d.status,
        d.medical_license_number,
        d.bio,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return hay.includes(s);
    });
  }, [docs, q]);

  return (
    <div className={`doc-dir-page ${isAdmin ? "admin" : "standalone"}`}>
      <div className="doc-dir-panel">
        <div className="doc-dir-header">
          <div className="doc-dir-header-inner">
            <div className="doc-dir-brand">
              <img className="doc-dir-logo" src={drIsInLogo} alt="Doctor Is In App" />
              <div>
                <h2 className="doc-dir-title">Advisor Connect™</h2>
                <div className="doc-dir-tagline">Professional Collaboration Network</div>
              </div>
            </div>

            <div className="doc-dir-actions">
              <button className="btn-secondary" type="button" onClick={handleBack}>
                {isAdmin ? "Back to Dashboard" : "Back to Advisor Portal"}
              </button>

              {!isAdmin ? (
                <button className="btn-danger" type="button" onClick={logout}>
                  Logout
                </button>
              ) : null}

              <button className="btn-secondary" type="button" onClick={() => void loadDoctors()} disabled={loading}>
                Refresh
              </button>
            </div>
          </div>

          <div className="doc-dir-intro">
            Find an available colleague by specialty, language, and location. The consultation window is private and separate from all student conversations.
          </div>

          <div className="doc-dir-searchbar">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search advisors by name, specialty, language, or location…" />
          </div>

          {err ? <div className="notice error">{err}</div> : null}
          {loading ? <div className="doc-dir-loading">Loading advisors...</div> : null}
        </div>

        <div className="doc-dir-body">
          {!loading && filtered.length === 0 ? <div className="doc-dir-empty muted">No matches.</div> : null}

          <div className="doc-dir-grid">
            {filtered.map((d) => {
              const name = cleanValue(d.name) || "Unknown Advisor";
              const license = cleanValue(d.medical_license_number) || "—";
              const specialty = cleanValue(d.specialties) || cleanValue(d.medical_field) || "—";
              const state = cleanValue(d.state);
              const country = cleanValue(d.country);
              const location = cleanValue(d.location) || [state, country].filter(Boolean).join(", ") || "—";
              const status = cleanValue(d.status) || (d.retired ? "Retired" : "On Call");
              const bio = (d.bio || "").trim();
              const availability = getAvailability(d);
              const emergencyConsult = boolFromAny(d.emergency_consult_available, availability === "available");
              const isLongBio = bio.length > 160;
              const isExpanded = !!expanded[String(d.id)];
              const photo = absoluteUrl(d.photo_url);
              const website = normalizeWebsite(d.website);

              return (
                <div key={d.id} className="doc-card">
                  <div className="doc-card-top">
                    <div className="doc-avatar">
                      {photo ? <img src={photo} alt={name} /> : <div className="doc-avatar-fallback"><span>{name.slice(0, 2).toUpperCase()}</span></div>}
                    </div>

                    <div className="doc-title">
                      <div className="doc-name">{name}</div>
                      <div className={`doc-pill ${availability}`}>{availabilityLabel(availability)}</div>
                    </div>
                  </div>

                  <div className="doc-meta">
                    <div className="doc-row"><span className="doc-label">License #:</span><span className="doc-value">{license}</span></div>
                    <div className="doc-row"><span className="doc-label">Specialty:</span><span className="doc-value">{specialty}</span></div>
                    <div className="doc-row"><span className="doc-label">Languages:</span><span className="doc-value">{formatLanguages(d.languages)}</span></div>
                    <div className="doc-row"><span className="doc-label">Location:</span><span className="doc-value">{location}</span></div>
                    <div className="doc-row"><span className="doc-label">Status:</span><span className="doc-value">{status}</span></div>
                    <div className="doc-row"><span className="doc-label">SOS Consult:</span><span className={`doc-value consult-${emergencyConsult ? "yes" : "no"}`}>{emergencyConsult ? "Available" : "Unavailable"}</span></div>
                  </div>

                  <div className="doc-bio">
                    <div className="doc-bio-title">Bio</div>
                    <div className="doc-bio-text">{bio ? (isExpanded ? bio : bio.slice(0, 160) + (isLongBio ? "…" : "")) : "—"}</div>
                    {isLongBio ? (
                      <button className="doc-link" type="button" onClick={() => setExpanded((prev) => ({ ...prev, [String(d.id)]: !prev[String(d.id)] }))}>
                        {isExpanded ? "See less" : "See more"}
                      </button>
                    ) : null}
                  </div>

                  <div className="doc-card-footer">
                    <div className="doc-admin-actions">
                      {!isAdmin ? (
                        <button type="button" className="doc-btn connect" onClick={() => handleConnect(d)} disabled={availability === "offline"}>
                          {availability === "offline" ? "Offline" : "Connect"}
                        </button>
                      ) : null}

                      <button type="button" className="btn-secondary" onClick={() => handleProfile(d)}>
                        Profile
                      </button>

                      {isAdmin ? (
                        <button type="button" className="btn-secondary" onClick={() => handleEdit(d)}>
                          Edit
                        </button>
                      ) : null}
                    </div>

                    <div className="doc-website-row">
                      {website ? <a className="dash-website" href={website} target="_blank" rel="noreferrer">Visit Website</a> : <span className="doc-website-placeholder" />}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="doc-dir-footer">© {year} Doctor Is In App. All rights reserved.</div>
        </div>
      </div>

      {connectDoctor ? (
        <div className="advisor-connect-overlay" role="dialog" aria-modal="true" aria-label="Advisor consultation" onClick={() => setConnectDoctor(null)}>
          <div className="advisor-connect-modal" onClick={(e) => e.stopPropagation()}>
            <div className="advisor-connect-modal-head">
              <div>
                <div className="advisor-connect-kicker">PRIVATE ADVISOR CONSULTATION</div>
                <h3>Connect with {cleanValue(connectDoctor.name) || "Advisor"}</h3>
              </div>
              <button type="button" className="advisor-connect-close" onClick={() => setConnectDoctor(null)}>✕</button>
            </div>

            <div className="advisor-connect-summary">
              <div><strong>Specialty:</strong> {cleanValue(connectDoctor.specialties) || cleanValue(connectDoctor.medical_field) || "—"}</div>
              <div><strong>Languages:</strong> {formatLanguages(connectDoctor.languages)}</div>
              <div><strong>Status:</strong> {availabilityLabel(getAvailability(connectDoctor))}</div>
            </div>

            <div className="advisor-connect-privacy">
              This consultation is separate from the student channel. Do not paste or forward the student’s confidential chat transcript. Share only the minimum anonymized background needed for professional guidance.
            </div>

            <label className="advisor-connect-label" htmlFor="consultation-message">Consultation request</label>
            <textarea
              id="consultation-message"
              value={consultMessage}
              onChange={(e) => {
                setConsultMessage(e.target.value);
                setConsultNotice("");
              }}
              placeholder="Example: I am handling an elevated-risk situation and would appreciate guidance on de-escalation and next assessment questions."
              rows={7}
            />

            {consultNotice ? <div className="advisor-connect-notice">{consultNotice}</div> : null}

            <div className="advisor-connect-modal-actions">
              <button type="button" className="btn-secondary" onClick={() => setConnectDoctor(null)}>Cancel</button>
              <button type="button" className="doc-btn connect" onClick={sendConsultationRequest}>Open Consultation Channel</button>
            </div>
          </div>
        </div>
      ) : null}

      {profileDoctor ? (
        <div className="advisor-connect-overlay" role="dialog" aria-modal="true" aria-label="Advisor profile" onClick={() => setProfileDoctor(null)}>
          <div className="advisor-profile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="advisor-connect-modal-head">
              <div><div className="advisor-connect-kicker">ADVISOR PROFILE</div><h3>{cleanValue(profileDoctor.name) || "Advisor"}</h3></div>
              <button type="button" className="advisor-connect-close" onClick={() => setProfileDoctor(null)}>✕</button>
            </div>
            <div className="advisor-profile-grid">
              <div><strong>Specialty</strong><span>{cleanValue(profileDoctor.specialties) || cleanValue(profileDoctor.medical_field) || "—"}</span></div>
              <div><strong>Languages</strong><span>{formatLanguages(profileDoctor.languages)}</span></div>
              <div><strong>Location</strong><span>{cleanValue(profileDoctor.location) || [cleanValue(profileDoctor.state), cleanValue(profileDoctor.country)].filter(Boolean).join(", ") || "—"}</span></div>
              <div><strong>Status</strong><span>{availabilityLabel(getAvailability(profileDoctor))}</span></div>
            </div>
            <div className="advisor-profile-bio">{cleanValue(profileDoctor.bio) || "No biography has been provided."}</div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
